import React, {Component, Fragment} from "react";
import PropTypes from "prop-types";
import {BoldButton, ItalicButton, UnderlineButton} from 'draft-js-buttons';
import {EditorState} from "draft-js";
import ReactPasswordStrength from "react-password-strength";
import moment from "moment";
import {TwitterPicker} from "react-color";
import Editor, {composeDecorators} from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import createRichButtonsPlugin from 'draft-js-richbuttons-plugin';
import createLinkPlugin from 'draft-js-anchor-plugin';
import createInlineToolbarPlugin from 'draft-js-inline-toolbar-plugin';
import createAlignmentPlugin from 'draft-js-alignment-plugin';
import createBlockDndPlugin from 'draft-js-drag-n-drop-plugin';
import 'draft-js-inline-toolbar-plugin/lib/plugin.css';
import 'draft-js-anchor-plugin/lib/plugin.css';
import 'draft-js-image-plugin/lib/plugin.css';
import {isMobileOnly} from "react-device-detect";

import {
	countMap,
	forEachObj,
	formatPrice,
	mapObject,
	mapObjectToArray,
	objectFilter,
	objectMerge,
	upperFirst
} from "../../lib/tools";
import {Button, DatePickerButton, FileSelect, HiddenSubmit, Image, SearchField} from "./common";
import {Modal} from "./modal";
import {FormContext} from "../../contexts";
import {editorFromHtml, editorFromText, editorToHtml, editorToText} from "../../lib/draft";
import {reduxWrapper} from "../../globalState";
import {getImage} from "../../lib/api";
// Icons
import arrowLeft from "../../resources/icons/left_arrow.svg";
import arrowRight from "../../resources/icons/right_arrow.svg";
import greenCheckMarkIcon from "../../resources/icons/checkmark-darkGreen.svg";
import CloseIcon from "../../resources/icons/x-black.svg"


const formValidationRules = {
	email: {
		type: "regexMatch",
		regex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
		error: "Venligst indtast korrekt email"
	},
	required: {
		type: "length",
		length: 1,
		error: ""
	},
	phone: {
		type: "regexMatch",
		regex: /^\s*(?:\d\s*){8}$/i,
		error: "Venligst indtast korrekt telefon nummer"
	},
	regNum: {
		type: "regexMatch",
		regex: /^\s*(?:\d\s*){4}$/i,
		error: "Venligst indtast korrekt reg nummer (4 cifre)"
	},
	accNum: {
		type: "regexMatch",
		regex: /^\s*(?:\d\s*){7,10}$/i,
		error: "Venligst indtast korrekt konto nummer (7-10 cifre)"
	}
};

const reqErrorText = "Dette felt er påkrævet";

function initFormElement(context = {}, props = {}, defaultValue, onChangeFunc, validate, storage) {
	const tempTitle = props.title || props.placeholder || null;
	const name = props.name || (tempTitle && tempTitle.toLowerCase().replace(" ", "")) || "value";
	const title = props.hasOwnProperty("title") ? props.title : tempTitle || upperFirst(name);
	const placeholder = props.placeholder || props.title || upperFirst(name);
	const deferred = !!props.deferred;
	const value = deferred && storage.timeout != null ? defaultValue : (props.hasOwnProperty("value") ? props.value : (context.getValue ? context.getValue(name) : (defaultValue)));
	const required = props.req || props.required;
	const reqError = required ? (value || value === 0 ? null : reqErrorText) : null;
	const error = reqError || (validate ? validate(value, required) : null);
	context.validate && context.validate(name, error);
	const classNames = props.className ? props.className.split(" ") : [];
	(props.locked || props.disabled) && classNames.push("locked");
	const submitOnEnter = context.submitOnEnter !== false;
	const onChange = val => {
		if (props.locked || props.disabled) return;
		if (props.formatChange)
			val = props.formatChange(val);
		
		onChangeFunc && onChangeFunc(val);
		
		if (deferred) {
			clearTimeout(storage.timeout);
			storage.timeout = setTimeout(() => {
				context.onChange && context.onChange(name, val);
				props.onChange && props.onChange(val);
			}, 1000)
		} else {
			context.onChange && context.onChange(name, val);
			props.onChange && props.onChange(val);
		}
	};
	const showErrors = props.showError == null ? (context.showErrors || false) : props.showError;
	
	return {
		name,
		title,
		placeholder,
		value,
		error: showErrors ? error : null,
		required,
		classNames,
		onChange,
		submitOnEnter,
		deferred
	};
}

//<editor-fold desc="ValidateForm">

export class ValidateForm extends React.Component {
	static propTypes = {
		isValid: PropTypes.func,
		onChange: PropTypes.func,
		onElementChange: PropTypes.func,
		onSubmit: PropTypes.func,
		showErrors: PropTypes.bool,
		values: PropTypes.object,
		clear: PropTypes.bool,
		child: PropTypes.bool,
		className: PropTypes.string,
		submitOnEnter: PropTypes.bool,
	};
	
	state = {
		values: {},
		showErrors: false
	};
	
	inputs = {};
	valid = true;
	
	constructor(props) {
		super(props);
		
		this.getValues = () => {
			let values = {...(this.props.hasOwnProperty("values") ? this.props.values : this.state.values)};
			forEachObj(this.inputs, (x, k) => values.hasOwnProperty(k) ? 0 : values[k] = null);
			return values;
		};
		
		this.getValue = (name) => {
			const values = this.getValues();
			if (!this.inputs.hasOwnProperty(name))
				this.inputs[name] = true;
			return values[name] || null;
		};
		
		this.validate = (name, error = null) => this.inputs[name] = !error;
		
		this.onChange = (name, value) => {
			const values = Object.assign({}, this.getValues(), {[name]: value});
			this.setState({values});
			this.props.onChange && this.props.onChange(values);
			this.props.onElementChange && this.props.onElementChange(name, value);
		};
		
		this.onSubmit = () => {
			if (this.valid) {
				if (this.props.onSubmit) {
					const values = mapObject(this.getValues(), (x, key) => ({
						key,
						data: x instanceof EditorState ? (x.text ? editorToText(x) : editorToHtml(x)) : x
					}));
					this.props.onSubmit(values);
					
					if (this.props.clear) {
						const empty = mapObject(values, (x, k) => ({key: k}));
						this.setState({values: empty, showErrors: false});
						this.props.onChange && this.props.onChange(empty);
					}
				}
			} else {
				this.setState({showErrors: true});
			}
		};
		
	}
	
	render() {
		const {children, showErrors, child, className, submitOnEnter} = this.props;
		
		this.inputs = {};
		
		const content = (
			<FormContext.Provider value={{
				getValue: this.getValue,
				validate: this.validate,
				onChange: this.onChange,
				onSubmit: this.onSubmit,
				showErrors: showErrors == null ? this.state.showErrors : showErrors,
				submitOnEnter
			}}>
				{children}
			</FormContext.Provider>
		);
		
		if (child) {
			return (
				<div className={["validateForm", className].join(" ")}>
					{content}
				</div>
			)
		} else {
			return (
				<form 
					className={["validateForm", className].join(" ")} 
					onSubmit={e => {
						e.preventDefault();
						this.onSubmit();
					}} 
					noValidate
				>
					{content}
					{submitOnEnter !== false ? <HiddenSubmit/> : null}
				</form>
			)
		}
	}
	
	componentDidUpdate() {
		this.valid = countMap(this.inputs, x => x ? 0 : 1) < 1;
		this.props.isValid && this.props.isValid(this.valid);
	}
}

//</editor-fold>

//<editor-fold desc="Form Input">

//An input like HTML inputs with a label and built in validation
export class FormInput extends React.Component {
	static propTypes = {
		name: PropTypes.string.isRequired,
		custom: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
		icon: PropTypes.string,
		locked: PropTypes.bool,
		onChange: PropTypes.func,
		password: PropTypes.bool,
		req: PropTypes.bool,
		required: PropTypes.bool,
		rules: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
		showError: PropTypes.bool,
		title: PropTypes.string,
		type: PropTypes.string,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	};
	
	static contextType = FormContext;
	
	state = {
		value: null
	};
	
	storage = {};
	
	valid = true;
	
	constructor(props) {
		super(props);
		
		this.validate = value => {
			if (!this.valid) return "Kodeordet er ikke stærkt nok";
			const {rules = [], custom = []} = this.props;
			value = value || "";
			
			try {
				(Array.isArray(rules) ? rules : [rules]).forEach(x => {
					const rule = formValidationRules[x];
					if (!rule) return;
					
					if (value.toString().length > 0 && !FormInput.testRule(value, rule))
						throw new Error(rule.error || "Dette er ikke korrekt :(")
				});
				
				(Array.isArray(custom) ? custom : [custom]).forEach(x => {
					const rule = x;
					if (!rule) return;
					
					if (value.toString().length > 0 && !FormInput.testRule(value, rule))
						throw new Error(rule.error || "Dette er ikke korrekt :(")
				});
			} catch (e) {
				return e.message;
			}
			return null;
		};
	}
	
	static testRule(value, rule) {
		switch (rule.type) {
			case "regexMatch":
				return rule.regex.test(value);
			case "length":
				return value.toString().length >= rule.length;
			case "compare":
				return value === rule.value;
			default:
				return true;
		}
	}
	
	render() {
		const {type, password, icon, autoComplete} = this.props;
		
		const {title, placeholder, value, error, required, classNames, onChange, name, submitOnEnter} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), this.validate, this.storage);
		
		classNames.push("formInput");
		
		if (password) {
			return (
				<div className="formInput">
					<div className="inputHead">
						<label>{title}<span className="required">*</span></label>
						{error && <span className="error">{error}</span>}
					</div>
					<ReactPasswordStrength
						inputProps={{placeholder: placeholder}}
						minLength={5}
						minScore={2}
						tooShortWord="Det kan du gøre bedre"
						scoreWords={['svagt', 'ikke godt', 'hmmm... okay', 'nice!', 'fantastisk!!']}
						changeCallback={({isValid}, val) => {
							this.valid = isValid;
							onChange(val && val.password)
						}}/>
				</div>
			)
		}
		
		return (
			<div className={classNames.join(" ")}>
				<div className="inputHead">
					<label>{title}{required && <span className="required">*</span>}</label>
					{error && <span className="error">{error}</span>}
				</div>
				<input
					type={type || "text"}
					name={name || "field"}
					value={value || ""}
					onKeyDown={e => !submitOnEnter && e.key === "Enter" && e.preventDefault()}
					required
					onChange={e => onChange(e.target.value)}
					style={{backgroundImage: `url(${icon})`}}
					placeholder={placeholder}
					autoComplete={autoComplete}
				/>
			</div>
		);
	}
}

//</editor-fold>

//<editor-fold desc="Form Text">

const richButtonsPlugin = createRichButtonsPlugin();
const {ItalicButton: IButton, BoldButton: BButton, OLButton, ULButton, H2Button, H3Button} = richButtonsPlugin;

const blockDndPlugin = createBlockDndPlugin();
const alignmentPlugin = createAlignmentPlugin();

const decorator = composeDecorators(
	alignmentPlugin.decorator,
	blockDndPlugin.decorator
);
const imagePlugin = createImagePlugin({decorator});

const linkPlugin = createLinkPlugin();

const inlineToolbarPlugin = createInlineToolbarPlugin({
	structure: [
		BoldButton,
		ItalicButton,
		linkPlugin.LinkButton
	]
});
const {InlineToolbar} = inlineToolbarPlugin;

//A text field for use in validation forms
export class FormText extends React.Component {
	static propTypes = {
		value: PropTypes.instanceOf(EditorState),
		onChange: PropTypes.func
	};
	
	static contextType = FormContext;
	
	state = {
		editor: EditorState.createEmpty()
	};
	
	storage = {};
	usedChars = 0;
	
	validate = (value, req) => {
		
		if (req) {
			if (value instanceof EditorState && !value.getCurrentContent().hasText()) return reqErrorText;
			if (this.usedChars < 1) return reqErrorText;
		}
		
		if (this.props.maxChar && this.usedChars > this.props.maxChar) return "Du er gået over bogstavsgrænsen";
		return null;
	};
	
	render() {
		const {text, maxChar, hideMaxChar} = this.props;
		const {value, error, classNames, onChange, title} =
			initFormElement(this.context, this.props, this.state.editor, editor => this.setState({editor}), this.validate, this.storage);
		
		classNames.push("wysiwyg", "inputHead");
		
		if (text && maxChar) {
			this.usedChars = editorToText(value).length;
		}
		
		return (
			<div className={classNames.join(" ")}>
				<label>{title} <span className="required">*</span></label>
		{!text || !maxChar || hideMaxChar ? null : <span className="maxChar">({this.usedChars} / {maxChar} tegn)</span>}
				{error && <span className="error">{error}</span>}
				{text || this.props.hideHead ? null :
					(<div className="head">
						<BButton/>
						<IButton/>
						<H2Button/>
						<H3Button/>
						<ULButton/>
						<OLButton/>
					</div>)
				}
				<Editor editorState={value ? (text ? editorFromText(value) : editorFromHtml(value)) : EditorState.createEmpty()}
				        onChange={val => {val.text = text; onChange(val);}}
				        plugins={text ? [] : [inlineToolbarPlugin, linkPlugin, imagePlugin, blockDndPlugin, alignmentPlugin, richButtonsPlugin]}/>
				{text ? null :
					<InlineToolbar>
						{props => (
							<Fragment>
								<BoldButton {...props}/>
								<ItalicButton {...props}/>
								<UnderlineButton {...props}/>
								<linkPlugin.LinkButton {...props}/>
							</Fragment>
						)}
					</InlineToolbar>}
			</div>
		);
	}
}

//A component used for clickable buttons in the text editor
const FormTextButton = ({clickHandler, code, icon}) => (
	<div className="button" onClick={() => clickHandler(code)} onMouseDown={e => e.preventDefault()}>
		{icon}
	</div>
);

FormTextButton.propTypes = {
	clickHandler: PropTypes.func.isRequired,
	code: PropTypes.string.isRequired,
	icon: PropTypes.string
};
//</editor-fold>

//<editor-fold desc="Dropdown">

//A searchable dropdown with both multiple and single value support
export class DropDown extends React.Component {
	static propTypes = {
		multiple: PropTypes.bool,
		onChange: PropTypes.func,
		onCreate: PropTypes.func,
		options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
		title: PropTypes.string,
		name: PropTypes.string,
		value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array])
	};
	
	static contextType = FormContext;
	
	state = {
		search: "",
		value: null,
		focus: false
	};
	
	storage = {};
	
	constructor(props) {
		super(props);
		
		this.validate = value => (this.props.req || this.props.required) ?
			Array.isArray(value) && value.length > 0 :
			null;
	}
	
	render() {
		const {options = {}, onCreate, multiple, externalLabel} = this.props;
		
		const {title, placeholder, value: val, error, required, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), this.validate, this.storage);
		
		classNames.push("dropdownWrapper");
		classNames.push(multiple ? "multiple" : "single");
		
		const value = multiple ? (val == null ? [] : (Array.isArray(val) ? val : [val])) : val;
		
		const onToggle = val => {
			let newVal = null;
			if (multiple) {
				newVal = [...value];
				const index = value.indexOf(parseInt(val, 10));
				if (index !== -1)
					newVal.splice(index, 1);
				else
					newVal.push(parseInt(val, 10));
			} else {
				// eslint-disable-next-line
				if (value == val)
					newVal = null;
				else {
					newVal = val;
					this.search.blur();
				}
			}
			
			onChange(newVal);
		};
		
		const onBackspace = e => {
			if (this.state.search.length < 1 && e.keyCode === 8) {
				if (multiple) {
					let newVal = [...value];
					if (value.length > 0) {
						newVal.splice(newVal.length - 1, 1);
						onChange(newVal);
					}
				} else if (value != null) {
					onChange(null);
				}
			}
		};
		
		const hasValue = val => {
			if (this.props.multiple)
				return value.indexOf(parseInt(val, 10)) !== -1;
			// eslint-disable-next-line
			return value == val;
		};
		
		const newOptions = this.state.search.length > 0 ?
			objectFilter(options, x => (x || "").toLowerCase().includes(this.state.search.toLowerCase())) :
			options;
		
		const empty = value == null || (Array.isArray(value) && value.length < 1);
		
		return (
			<div className={classNames.join(" ")}>
				{externalLabel && <label>{title}{required ? "*" : ""}</label>}
				{error && <span className="error">{error}</span>}
				<div className="selections">
					{multiple ?
						value.map(x =>
							<div className="dropdownItem" key={x}>
								<span className="remove" onMouseDown={e => e.preventDefault()}
								      onClick={() => onToggle(x)}>x</span>
								{options[x] || "..."}
							</div>
						) :
						(!this.state.focus && value != null &&
							<span className="selection">{`${options[value]} kr` || "..."}</span>)
					}
					
					<input type="search" value={this.state.search}
					       onChange={e => this.setState({search: e.target.value || ""})}
					       onKeyDown={onBackspace} onFocus={() => this.setState({focus: true})}
					       onBlur={() => this.setState({focus: false, search: ""})} ref={r => this.search = r}/>
				</div>
				
				{
					!externalLabel && !this.state.focus &&
					((Array.isArray(value) && value.length < 1) || value == null) &&
					<span className="placeholder">{placeholder}{required ? "*" : ""}</span>
				}
				
				<div className="options" style={{display: this.state.focus ? "" : "none"}}>
					<span key="empty"
					      className={"empty" + (empty ? " selected" : "")}
					      onMouseDown={e => e.preventDefault()}
					      onClick={() => onChange(null)}>Intet valgt</span>
					{mapObjectToArray(newOptions, (x, i) =>
						<span key={i}
						      className={hasValue(i) ? "selected" : ""}
						      onMouseDown={e => e.preventDefault()}
						      onClick={() => onToggle(i)}>{x} kr</span>
					)}
					
					{
						this.state.search.length > 1 && Object.keys(newOptions).length < 1 && onCreate &&
						<span key="new" onMouseDown={e => e.preventDefault()}
						      onClick={() => onCreate(this.state.search).then(id => {
							      onToggle(id);
							      this.setState({search: ""})
						      })}>Add '{this.state.search}'</span>
					}
				</div>
			</div>
		);
	}
}

//</editor-fold>

//<editor-fold desc="Image Select">

//A component for selecting one image out of a list
export class ImageSelect extends React.Component {
	static propTypes = {
		images: PropTypes.object,
		onAdd: PropTypes.func,
		onChange: PropTypes.func,
		value: PropTypes.any
	};
	
	static contextType = FormContext;
	
	state = {
		showModal: false,
		value: null
	};
	
	storage = {};
	
	render() {
		// const {images, onAdd, onRemove} = this.props;
		const {images} = this.props;
		const {value, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), this.validate, this.storage);
		
		classNames.push("imageSelect");
		
		return (
			<Fragment>
				<div className={classNames.join(" ")} onClick={() => this.setState({showModal: true})}>
					{images && value ? <Image src={getImage(value)} alt="temp"/> : <Image src={CloseIcon}/>}
				</div>
				{this.state.showModal && <ImageModal images={images} onSelect={onChange}
				                                     onHide={() => this.setState({showModal: false})}/>}
			</Fragment>
		);
	}
}

class ImageModal extends React.Component {
	constructor(props) {
		super(props);
		
		this.state = {newImage: null, url: ""};
		
		this.onSelect = (newImage) => {
			this.props.onSelect(newImage);
			this.props.onHide();
		};
	}
	
	render() {
		const {images, onHide} = this.props;
		return (
			<Modal onHide={onHide} className="imageModal">
				<div className="imageWrapper">
					<div className="image" onClick={() => this.onSelect(null)}>
						<img src={CloseIcon} alt="Nothing"/>
					</div>
					{mapObjectToArray(images, (x, i) => (
						<div key={i} className="image" onClick={() => this.onSelect(x)}>
							<Image src={getImage(x)}/>
						</div>
					))}
				</div>
			</Modal>
		);
	}
}

ImageModal.propTypes = {
	className: PropTypes.string,
	images: PropTypes.object.isRequired,
	onAdd: PropTypes.func,
	onHide: PropTypes.func,
	onSelect: PropTypes.func.isRequired
};

//</editor-fold>

//<editor-fold desc="Checkbox">
//A simple checkbox component with a label
export class Checkbox extends Component {
	static propTypes = {
		value: PropTypes.any,
		onChange: PropTypes.func,
		icon: PropTypes.string,
		className: PropTypes.string,
		title: PropTypes.node,
		image: PropTypes.string,
		noLabel: PropTypes.bool
	};
	
	static contextType = FormContext;
	
	state = {
		value: false
	};
	
	storage = {};
	
	render() {
		const {icon, image, noLabel} = this.props;
		const {title, value, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
		
		classNames.push("customCheckbox");
		classNames.push(value ? "active" : "inactive");
		
		return (
			<label onClick={() => onChange(!value)} className="checkbox">
				<div className={classNames.join(" ")}>
					<img src={icon || greenCheckMarkIcon} alt="checkbox" className={icon == null ? "tick" : "icon"}/>
				</div>
				{noLabel ? null : (
					<Fragment>
						{image && <img src={image}/>}
						<p>{title}</p>
					</Fragment>
				)}
			</label>
		);
	}
}

//</editor-fold>

//<editor-fold desc="Form Group">

//A subgroup of inputs in a Validate Form used for grouping inputs
export class FormGroup extends React.Component {
	static propTypes = {
		children: PropTypes.node.isRequired,
		className: PropTypes.string,
		name: PropTypes.string.isRequired
	};
	
	static contextType = FormContext;
	
	inputs = {};
	valid = true;
	
	storage = {};
	
	constructor(props) {
		super(props);
		
		this.getValues = () => {
			let values = this.context.getValue(this.props.name) || {};
			forEachObj(this.inputs, (x, k) => values.hasOwnProperty(k) ? 0 : values[k] = null);
			return values;
		};
		
		this.getValue = name => {
			const values = this.getValues();
			if (!this.inputs.hasOwnProperty(name))
				this.inputs[name] = true;
			return values[name] || null;
		};
		
		this.validate = (name, error = null) => this.inputs[name] = !error;
		
		this.onChange = (name, value) => {
			const values = objectMerge(this.getValues(), {[name]: value});
			this.context.onChange(this.props.name, values);
		};
	}
	
	
	render() {
		if (!this.context) return null;
		
		const {children} = this.props;
		let newContext = {...this.context};
		
		newContext.getValue = this.getValue;
		newContext.validate = this.validate;
		newContext.onChange = this.onChange;
		
		return (
			<FormContext.Provider value={newContext}>
				{children}
			</FormContext.Provider>
		);
	}
	
	componentDidUpdate() {
		this.valid = countMap(this.inputs, x => x ? 0 : 1) < 1;
		this.context.validate(this.props.name, this.valid ? null : "error");
	}
}

//</editor-fold>

//<editor-fold desc="Datetime Input">
export class DatetimeInput extends Component {
	static propTypes = {
		value: PropTypes.any,
		title: PropTypes.any,
		req: PropTypes.any,
		showError: PropTypes.any,
		onChange: PropTypes.any,
		date: PropTypes.bool,
		time: PropTypes.bool
	};
	
	static contextType = FormContext;
	
	state = {
		value: null
	};
	
	storage = {};
	
	render() {
		let {date = true, time = false, placeholder, disabled} = this.props;
		const {title, value, error, required, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
		
		classNames.push("dateInput");
		
		return (
			<div className={classNames.join(" ")}>
				<div className="inputHead">
					<label>{title}{required && <span className="required">*</span>}</label>
					{error && <span className="error">{error}</span>}
				</div>
				{
					date ?
						(
							time ?
								<DatePickerButton
									selected={value}
									minDate={moment()}
									onChange={onChange}
									showTimeSelect
									timeFormat="HH:mm"
									timeIntervals={60}
									dateFormat="LLL"
									timeCaption="Tid"
									dropdownMode="scroll"
									withPortal={ isMobileOnly ? true : false }
									fakePlaceholder={placeholder}
									disabled={disabled}/> :
								<DatePickerButton
									selected={value}
									onChange={onChange}
									minDate={moment()}
									dropdownMode="scroll"
									disabled={disabled}
									fakePlaceholder={placeholder}
									/>
						) :
						<DatePickerButton
							selected={value}
							onChange={onChange}
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={60}
							timeFormat="HH:mm"
							timeCaption="Tid"
							dropdownMode="scroll"
							disabled={disabled}
							fakePlaceholder={placeholder}/>
				}
			</div>
		);
	}
}

//</editor-fold>

//A color picker component
export class HoverColorPicker extends Component {
	
	static contextType = FormContext;
	
	state = {
		value: ""
	};
	
	storage = {};
	
	render() {
		const {value, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
		
		classNames.push("colorPickerWrapper");
		
		return (
			<div className={classNames.join(" ")}>
				<div className="hoverColorPicker" style={{backgroundColor: value}}>
					<TwitterPicker color={value} onChangeComplete={val => onChange(val.hex)}
					               colors={['#FF6900', '#FCB900', '#7BDCB5', '#00D084', '#8ED1FC', '#0693E3', '#ABB8C3', '#EB144C', '#F78DA7', '#000000']}
					               triangle="top-right"/>
				</div>
			</div>
		);
	}
}

export class FormList extends React.Component {
	
	static contextType = FormContext;
	
	state = {
		value: ""
	};
	
	storage = {};
	
	render() {
		const {children} = this.props;
		const {value: val, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
		classNames.push("formList");
		const value = val && Array.isArray(val) ? val : [];
		
		let newContext = {...this.context};
		
		newContext.getValue = null;
		newContext.validate = null;
		newContext.onChange = null;
		newContext.onSubmit = null;
		newContext.submitOnEnter = false;
		
		return (
			<div className={classNames.join(" ")}>
				{value.map((x, i) => (
					<div className="formListRow" key={i}>
						<Button className="close" onClick={() => {
							let arr = value;
							arr.splice(i, 1);
							onChange(arr)
						}}>x</Button>
						<FormContext.Provider>
							{children({value: x, index: i, onChange: val => {
								let arr = value;
								arr[i] = val;
								onChange(arr);
							}})}
						</FormContext.Provider>
					</div>
				))}
				<div className="formListRow add">
					<Button onClick={() => {
						let arr = value;
						arr.push(null);
						onChange(arr)
					}}>Add row</Button>
				</div>
			</div>
		);
	}
}

class ProductSelectRender extends React.Component {
	
	static contextType = FormContext;
	
	state = {
		search: "",
		products: [],
		showModal: false,
		value: null,
		page: 1
	};
	
	page = 1;
	
	storage = {};
	
	constructor(props) {
		super(props);
		
		this.onSubmit = () => {
			this.props.apiCall("GET", "/products", data => {
				this.setState({products: data})
			}, {
				search: this.state.search,
				limit: 6,
				limitOffset: (this.page - 1) * 6,
				simple: true
			})
		};
		
		this.newPage = page => {
			if (page > 0) {
				this.setState({page});
				this.page = page;
				this.onSubmit();
			}
		}
	}
	
	
	render() {
		const {value, classNames, onChange} =
			initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
		
		const selectProduct = id => {
			this.props.cacheProduct({product: id})
				.then(() => {
					onChange(id);
					this.setState({showModal: false})
				});
		};
		
		classNames.push("productSelectPreview");
		
		const product = (value && this.props.products[value]) || {thumbnail: null, name: "Choose a product"};
		return (
			<Fragment>
				<div className={classNames.join(" ")} onClick={() => this.setState({showModal: true})}>
					<Image src={product.thumbnail}/>
					<p className="name">{product.name}</p>
				</div>
				<Modal className="productSelectModal" show={this.state.showModal} onHide={() => this.setState({showModal: false})}>
					<div className="search">
						<SearchField value={this.state.search} onChange={val => this.setState({search: val})} onSubmit={this.onSubmit}/>
					</div>
					<div className="products">
						{this.state.products.map((x, i) => (
							<div className="product" key={i} onClick={() => selectProduct(x.id)}>
								<Image src={x.thumbnail}/>
								<p className="name">{x.name}</p>
								<p className="price">{formatPrice(x.price)}</p>
							</div>
						))}
					</div>
					<div className="navBar">
						<Button icon={arrowLeft} onClick={() => this.newPage(this.state.page - 1)}/>
						<p className="page">Page {this.state.page}</p>
						<Button icon={arrowRight} onClick={() => this.newPage(this.state.page + 1)}/>
					</div>
				</Modal>
			</Fragment>
		);
	}
}

export const ProductSelect = reduxWrapper("getApiCall", "loadProducts", "getCacheProduct")(ProductSelectRender);

class ImageUploadRender extends React.Component {
	
	static contextType = FormContext;
	
	state = {
		showModal: false,
		value: ""
	};
	
	storage = {};
	
    constructor(props) {
        super(props);
        
        this.uploadUrl = (url, onChange) => {
            this.props.apiCall("POST", "admin/images", id => onChange(id), {url});
        };
        
        this.uploadFile = (file, onChange) => {
	        this.props.apiCall("POST", "admin/images", id => onChange(id), null, null, {image: file});
        };
    }
    
    render() {
	    const {value, classNames, onChange} =
		    initFormElement(this.context, this.props, this.state.value, value => this.setState({value}), null, this.storage);
	
	    classNames.push("imageUploadPreview");
	    
        return (
	        <Fragment>
		        <div className={classNames.join(" ")} onClick={() => this.setState({showModal: true})}>
			        <Image src={getImage(value)}/>
		        </div>
		        <Modal className="imageUploadModal" show={this.state.showModal} onHide={() => this.setState({showModal: false})}>
			        <h2>Preview</h2>
			        <Image src={getImage(value)}/>
					
			        <h2>Url</h2>
			        <ValidateForm onSubmit={({url}) => this.uploadUrl(url, onChange)} clear child>
				        <FormInput type="url" name="url" title=""/>
				        <Button submit>Add</Button>
			        </ValidateForm>
			        
			        <h2>File</h2>
			        <FileSelect onUpload={file => this.uploadFile(file, onChange)} accept="image/*"/>
			        
		        </Modal>
	        </Fragment>
        );
    }
}

export const ImageUpload = reduxWrapper("getApiCall")(ImageUploadRender);


// WEBPACK FOOTER //
// ./src/components/common/formValidation.js